import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/layout/DashboardLayout';
import VisitExecutionPanel from '../components/VisitExecutionPanel';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  ArrowLeft,
  MapPin,
  Calendar,
  Clock,
  Users,
  Play,
  Square,
  CheckCircle,
  AlertTriangle,
  FileText,
  Camera,
  ClipboardCheck,
  Plus,
  Zap,
  ListChecks,
  Edit,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const visitTypeLabels = {
  routine_inspection: 'Routine Inspection',
  audit: 'Audit / Compliance',
  troubleshooting: 'Troubleshooting',
  post_outage: 'Post-Outage',
  preventive_review: 'Preventive Review',
  emergency: 'Emergency',
};

const statusColors = {
  scheduled: 'bg-sky-100 text-sky-700 border-sky-300',
  in_progress: 'bg-amber-100 text-amber-700 border-amber-300',
  completed: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  cancelled: 'bg-slate-100 text-slate-600 border-slate-300',
};

const taskResultColors = {
  pass: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  fail: 'bg-red-100 text-red-700 border-red-300',
  observation: 'bg-amber-100 text-amber-700 border-amber-300',
  pending: 'bg-slate-100 text-slate-600 border-slate-300',
};

export default function SiteVisitDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFindingDialog, setShowFindingDialog] = useState(false);
  const [showCheckoutDialog, setShowCheckoutDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editPlannedDate, setEditPlannedDate] = useState('');
  const [editPersonnel, setEditPersonnel] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [newFinding, setNewFinding] = useState({ description: '', severity: 'medium' });
  const [checkoutData, setCheckoutData] = useState({ report_summary: '', recommendations: [] });
  const [newRecommendation, setNewRecommendation] = useState('');

  const { hasRole, hasPermission } = useAuth();

  const fetchVisit = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/site-visits/${id}`);
      setVisit(response.data);
    } catch (error) {
      console.error('Failed to fetch visit:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchVisit();
  }, [fetchVisit]);

  const handleCheckIn = async () => {
    try {
      await axios.post(`${API}/site-visits/${id}/check-in`);
      fetchVisit();
    } catch (error) {
      console.error('Failed to check in:', error);
    }
  };

  const handleCheckOut = async () => {
    try {
      await axios.post(`${API}/site-visits/${id}/check-out`, checkoutData);
      setShowCheckoutDialog(false);
      fetchVisit();
    } catch (error) {
      console.error('Failed to check out:', error);
    }
  };

  const handleAddFinding = async () => {
    const findings = [...(visit.findings || []), { 
      id: Date.now().toString(), 
      ...newFinding,
      linked_asset_id: visit.linked_asset_ids?.[0] || null
    }];
    try {
      await axios.patch(`${API}/site-visits/${id}`, { findings });
      setShowFindingDialog(false);
      setNewFinding({ description: '', severity: 'medium' });
      fetchVisit();
    } catch (error) {
      console.error('Failed to add finding:', error);
    }
  };

  const handleGenerateActions = async () => {
    try {
      const response = await axios.post(`${API}/site-visits/${id}/generate-actions`);
      toast.success(`Generated ${response.data.generated_actions} action plans from findings`);
      fetchVisit();
    } catch (error) {
      console.error('Failed to generate actions:', error);
    }
  };

  const openEditDialog = () => {
    setEditPlannedDate(visit.planned_date || '');
    setEditPersonnel((visit.assigned_personnel || []).join(', '));
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    const payload = {
      planned_date: editPlannedDate,
      assigned_personnel: editPersonnel.split(',').map((s) => s.trim()).filter(Boolean),
      updated_at: new Date().toISOString(),
    };
    try {
      await axios.patch(`${API}/site-visits/${id}`, payload);
      setShowEditDialog(false);
      fetchVisit();
    } catch (err) {
      console.error('Failed to update site visit:', err);
      toast.error(err.response?.data?.detail || 'Failed to update site visit');
    }
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/site-visits/${id}`);
      setShowDeleteDialog(false);
      toast.success('Site visit deleted: Visit cancelled and marked deleted. Review associated action plans.');
      navigate('/site-operations');
    } catch (err) {
      console.error('Failed to delete site visit:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete site visit');
    }
  };

  const handleTaskStatusChange = async (taskId, status) => {
    const updatedTasks = visit.tasks.map((t) =>
      t.id === taskId ? { ...t, status } : t
    );
    try {
      await axios.patch(`${API}/site-visits/${id}`, { tasks: updatedTasks });
      fetchVisit();
    } catch (error) {
      console.error('Failed to update task:', error);
    }
  };

  const handleChecklistItemToggle = async (taskId, itemId) => {
    const updatedTasks = visit.tasks.map((task) => {
      if (task.id === taskId) {
        const updatedChecklist = task.checklist.map((item) =>
          item.id === itemId ? { ...item, completed: !item.completed } : item
        );
        return { ...task, checklist: updatedChecklist };
      }
      return task;
    });
    try {
      await axios.patch(`${API}/site-visits/${id}`, { tasks: updatedTasks });
      fetchVisit();
    } catch (error) {
      console.error('Failed to update checklist:', error);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  if (!visit) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-slate-500">Site visit not found</p>
          <Button asChild className="mt-4">
            <Link to="/site-operations">Back to Site Operations</Link>
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const completedTasks = visit.tasks?.filter((t) => t.status === 'completed').length || 0;
  const totalTasks = visit.tasks?.length || 0;
  const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/site-operations" data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Link>
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="font-heading text-2xl font-bold text-slate-900">{visit.visit_number}</h1>
                <Badge variant="outline" className={cn(statusColors[visit.status || 'scheduled'])}>
                  {(visit.status || 'scheduled').replace('_', ' ').toUpperCase()}
                </Badge>
              </div>
              <p className="text-slate-500">{visit.purpose}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {visit.status === 'scheduled' && (
              <Button onClick={handleCheckIn} data-testid="check-in-btn">
                <Play className="w-4 h-4 mr-2" />
                Check In
              </Button>
            )}
            {visit.status === 'in_progress' && (
              <Button onClick={() => setShowCheckoutDialog(true)} data-testid="check-out-btn">
                <Square className="w-4 h-4 mr-2" />
                Check Out
              </Button>
            )}
            {visit.status === 'completed' && visit.findings?.length > 0 && (
              <Button onClick={handleGenerateActions} variant="outline" data-testid="generate-actions-btn">
                <Zap className="w-4 h-4 mr-2" />
                Generate Actions
              </Button>
            )}
            {visit.status !== 'cancelled' && (hasRole('org_admin') || hasPermission?.('site_visit.update')) && (
              <Button variant="outline" onClick={openEditDialog} data-testid="edit-visit-btn">
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
            )}
            {visit.status !== 'cancelled' && (hasRole('org_admin') || hasPermission?.('site_visit.delete')) && (
              <Button variant="destructive" onClick={() => setShowDeleteDialog(true)} data-testid="delete-visit-btn">
                Delete
              </Button>
            )}
          </div>
        </div>

        {/* Visit Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-slate-500 mb-1">
                <MapPin className="w-4 h-4" />
                <span className="text-sm">Site</span>
              </div>
              <p className="font-medium text-slate-900">{visit.site}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-slate-500 mb-1">
                <Calendar className="w-4 h-4" />
                <span className="text-sm">Planned Date</span>
              </div>
              <p className="font-medium text-slate-900">{visit.planned_date}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-slate-500 mb-1">
                <Clock className="w-4 h-4" />
                <span className="text-sm">Duration</span>
              </div>
              <p className="font-medium text-slate-900">{visit.planned_duration}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-slate-500 mb-1">
                <Users className="w-4 h-4" />
                <span className="text-sm">Personnel</span>
              </div>
              <p className="font-medium text-slate-900">{visit.assigned_personnel?.join(', ') || 'None assigned'}</p>
            </CardContent>
          </Card>
        </div>

        {/* Progress */}
        {totalTasks > 0 && (
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">Task Progress</span>
                <span className="text-sm text-slate-500">{completedTasks}/{totalTasks} tasks completed</span>
              </div>
              <Progress value={progress} className="h-2" />
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs defaultValue={visit.tasks?.some(t => t.checks?.length > 0) ? "execute" : "tasks"} className="space-y-4">
          <TabsList>
            {visit.tasks?.some(t => t.checks?.length > 0) && (
              <TabsTrigger value="execute" data-testid="tab-execute">
                <ListChecks className="w-4 h-4 mr-2" />
                Execute
              </TabsTrigger>
            )}
            <TabsTrigger value="tasks" data-testid="tab-tasks">
              <ClipboardCheck className="w-4 h-4 mr-2" />
              Tasks ({visit.tasks?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="findings" data-testid="tab-findings">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Findings ({visit.findings?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="report" data-testid="tab-report">
              <FileText className="w-4 h-4 mr-2" />
              Report
            </TabsTrigger>
          </TabsList>

          {/* Execute Tab - New Execution Panel */}
          <TabsContent value="execute">
            <VisitExecutionPanel
              visit={visit}
              onUpdate={fetchVisit}
              onComplete={() => navigate('/site-operations')}
            />
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks">
            <Card className="border-slate-200">
              <CardHeader className="border-b border-slate-100 pb-4">
                <CardTitle className="font-heading text-lg">Site Tasks</CardTitle>
              </CardHeader>
              <CardContent className="pt-4 divide-y divide-slate-100">
                {visit.tasks?.length === 0 ? (
                  <p className="text-slate-500 py-4 text-center">No tasks defined for this visit</p>
                ) : (
                  visit.tasks?.map((task) => (
                    <div key={task.id} className="py-4 first:pt-0 last:pb-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{task.name}</span>
                            {task.category && (
                              <Badge variant="outline" className="text-xs">{task.category}</Badge>
                            )}
                            <Badge variant="outline" className={cn('text-xs', taskResultColors[task.result || task.status || 'pending'])}>
                              {(task.result || task.status || 'pending').toUpperCase()}
                            </Badge>
                          </div>
                          {task.description && (
                            <p className="text-sm text-slate-500 mt-1">{task.description}</p>
                          )}
                          {task.notes && (
                            <p className="text-sm text-slate-500 mt-1">{task.notes}</p>
                          )}
                          {/* Show checks count if available */}
                          {task.checks?.length > 0 && (
                            <p className="text-xs text-slate-400 mt-1">
                              {task.checks.filter(c => c.response !== null && c.response !== undefined).length}/{task.checks.length} checks completed
                            </p>
                          )}
                        </div>
                        {(visit.status === 'in_progress' || visit.status === 'scheduled') && (
                          <Select
                            value={task.status || 'pending'}
                            onValueChange={(v) => handleTaskStatusChange(task.id, v)}
                          >
                            <SelectTrigger className="w-[130px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                              <SelectItem value="skipped">Skipped</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                      {/* Checklist */}
                      {task.checklist?.length > 0 && (
                        <div className="mt-3 ml-4 space-y-2">
                          {task.checklist.map((item) => (
                            <div key={item.id} className="flex items-center gap-2">
                              <Checkbox
                                id={`${task.id}-${item.id}`}
                                checked={item.completed}
                                onCheckedChange={() => handleChecklistItemToggle(task.id, item.id)}
                                disabled={visit.status !== 'in_progress'}
                              />
                              <label
                                htmlFor={`${task.id}-${item.id}`}
                                className={cn('text-sm', item.completed && 'line-through text-slate-400')}
                              >
                                {item.description}
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Findings Tab */}
          <TabsContent value="findings">
            <Card className="border-slate-200">
              <CardHeader className="border-b border-slate-100 pb-4 flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-lg">Findings & Issues</CardTitle>
                {visit.status === 'in_progress' && (
                  <Button size="sm" onClick={() => setShowFindingDialog(true)} data-testid="add-finding-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Finding
                  </Button>
                )}
              </CardHeader>
              <CardContent className="pt-4 divide-y divide-slate-100">
                {visit.findings?.length === 0 ? (
                  <p className="text-slate-500 py-4 text-center">No findings recorded</p>
                ) : (
                  visit.findings?.map((finding) => (
                    <div key={finding.id} className="py-4 first:pt-0 last:pb-0">
                      <div className="flex items-start gap-3">
                        <div className={cn(
                          'p-1.5 rounded-full',
                          finding.severity === 'critical' ? 'bg-red-100' : finding.severity === 'high' ? 'bg-orange-100' : 'bg-amber-100'
                        )}>
                          <AlertTriangle className={cn(
                            'w-4 h-4',
                            finding.severity === 'critical' ? 'text-red-600' : finding.severity === 'high' ? 'text-orange-600' : 'text-amber-600'
                          )} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={cn(
                              'text-xs',
                              finding.severity === 'critical' ? 'bg-red-100 text-red-700 border-red-300' :
                              finding.severity === 'high' ? 'bg-orange-100 text-orange-700 border-orange-300' :
                              'bg-amber-100 text-amber-700 border-amber-300'
                            )}>
                              {finding.severity.toUpperCase()}
                            </Badge>
                          </div>
                          <p className="text-slate-900 mt-1">{finding.description}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Report Tab */}
          <TabsContent value="report">
            <Card className="border-slate-200">
              <CardHeader className="border-b border-slate-100 pb-4">
                <CardTitle className="font-heading text-lg">Visit Report</CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                {visit.check_in_time && (
                  <div className="flex items-center gap-4 p-3 bg-emerald-50 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="text-sm font-medium text-emerald-800">Checked In</p>
                      <p className="text-xs text-emerald-600">{new Date(visit.check_in_time).toLocaleString()}</p>
                    </div>
                  </div>
                )}
                {visit.check_out_time && (
                  <div className="flex items-center gap-4 p-3 bg-emerald-50 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="text-sm font-medium text-emerald-800">Checked Out</p>
                      <p className="text-xs text-emerald-600">{new Date(visit.check_out_time).toLocaleString()}</p>
                    </div>
                  </div>
                )}
                {visit.report_summary ? (
                  <div>
                    <h4 className="font-medium text-slate-900 mb-2">Summary</h4>
                    <p className="text-slate-600">{visit.report_summary}</p>
                  </div>
                ) : (
                  <p className="text-slate-500">No report generated yet. Complete the visit to generate a report.</p>
                )}
                {visit.recommendations?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-slate-900 mb-2">Recommendations</h4>
                    <ul className="list-disc list-inside space-y-1">
                      {visit.recommendations.map((rec, i) => (
                        <li key={i} className="text-slate-600">{rec}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {visit.generated_action_ids?.length > 0 && (
                  <div className="p-3 bg-sky-50 rounded-lg">
                    <p className="text-sm font-medium text-sky-800">
                      {visit.generated_action_ids.length} action plan(s) generated from this visit
                    </p>
                    <Link to="/action-plans" className="text-sm text-sky-600 hover:underline">
                      View Action Plans →
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Add Finding Dialog */}
        <Dialog open={showFindingDialog} onOpenChange={setShowFindingDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Finding</DialogTitle>
              <DialogDescription>Record an issue or observation from the site visit.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Severity</Label>
                <Select value={newFinding.severity} onValueChange={(v) => setNewFinding({ ...newFinding, severity: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Description</Label>
                <Textarea
                  value={newFinding.description}
                  onChange={(e) => setNewFinding({ ...newFinding, description: e.target.value })}
                  placeholder="Describe the finding..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowFindingDialog(false)}>Cancel</Button>
              <Button onClick={handleAddFinding}>Add Finding</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Visit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="sm:max-w-[525px]">
            <DialogHeader>
              <DialogTitle>Edit Site Visit</DialogTitle>
              <DialogDescription>Edit planned date and assigned personnel.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Planned Date</Label>
                <Input type="date" value={editPlannedDate || ''} onChange={(e) => setEditPlannedDate(e.target.value)} />
              </div>
              <div className="grid gap-2">
                <Label>Assigned Personnel</Label>
                <Input value={editPersonnel} onChange={(e) => setEditPersonnel(e.target.value)} placeholder="Comma separated names or emails" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveEdit}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="sm:max-w-[420px]">
              <DialogHeader>
                <DialogTitle>Delete Site Visit</DialogTitle>
                <DialogDescription>
                  This will cancel the visit and mark it as deleted. Any associated action plans will remain linked — please review them after deletion.
                </DialogDescription>
              </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
              <Button variant="destructive" onClick={confirmDelete}>Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Check Out Dialog */}
        <Dialog open={showCheckoutDialog} onOpenChange={setShowCheckoutDialog}>
          <DialogContent className="sm:max-w-[525px]">
            <DialogHeader>
              <DialogTitle>Complete Site Visit</DialogTitle>
              <DialogDescription>Summarize the visit and add recommendations.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Visit Summary</Label>
                <Textarea
                  value={checkoutData.report_summary}
                  onChange={(e) => setCheckoutData({ ...checkoutData, report_summary: e.target.value })}
                  placeholder="Summarize key observations and outcomes..."
                  rows={4}
                />
              </div>
              <div className="grid gap-2">
                <Label>Recommendations</Label>
                <div className="flex gap-2">
                  <Input
                    value={newRecommendation}
                    onChange={(e) => setNewRecommendation(e.target.value)}
                    placeholder="Add a recommendation..."
                  />
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (newRecommendation) {
                        setCheckoutData({
                          ...checkoutData,
                          recommendations: [...checkoutData.recommendations, newRecommendation]
                        });
                        setNewRecommendation('');
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>
                {checkoutData.recommendations.length > 0 && (
                  <ul className="list-disc list-inside text-sm text-slate-600 mt-2">
                    {checkoutData.recommendations.map((rec, i) => (
                      <li key={i}>{rec}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCheckoutDialog(false)}>Cancel</Button>
              <Button onClick={handleCheckOut}>Complete Visit</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
